// Utility / service vocabulary for equipment connections (EquipmentConnections
// and EquipmentLibraryConnections, db/24_process_equipment.sql — the DB header
// has pointed at this file as the owner of labels and units since the table
// was created; the P2 audit (2026-09-12) found it had never been written).
//
// UtilityType is the discriminator on a connection row: 'electrical' rows use
// the volts/phase/FLA/MCA/MOCP/HP/KW columns, every other type uses the
// flow/pressure/branch-size/consumption columns. The keys below are the only
// values the UI writes; the DB stores the key, this file owns how it reads.
//
//   kind  'electrical' | 'gas' | 'air' | 'steam' | 'liquid' | 'drain'
//   flowUnits / pressureUnits — the default units a new connection of this
//   type starts with (an estimator can change them per row).

export const UTILITY_TYPES = [
  { key: "electrical",      label: "Electrical",                kind: "electrical" },
  { key: "cca",             label: "Compressed air (CCA)",      kind: "gas",    flowUnits: "scfm",   pressureUnits: "psig" },
  { key: "nitrogen",        label: "Nitrogen (N₂)",             kind: "gas",    flowUnits: "scfm",   pressureUnits: "psig" },
  { key: "co2",             label: "Carbon dioxide (CO₂)",      kind: "gas",    flowUnits: "scfm",   pressureUnits: "psig" },
  { key: "oxygen",          label: "Oxygen (O₂)",               kind: "gas",    flowUnits: "scfm",   pressureUnits: "psig" },
  { key: "vacuum",          label: "Vacuum",                    kind: "gas",    flowUnits: "scfm",   pressureUnits: "in_hg" },
  { key: "dust_collection", label: "Dust collection",           kind: "air",    flowUnits: "cfm",    pressureUnits: "in_wc" },
  { key: "plant_steam",     label: "Plant steam",               kind: "steam",  flowUnits: "lbs_hr", pressureUnits: "psig" },
  { key: "clean_steam",     label: "Clean steam",               kind: "steam",  flowUnits: "kg_hr",  pressureUnits: "psig" },
  { key: "natural_gas",     label: "Natural gas",               kind: "gas",    flowUnits: "cfh",    pressureUnits: "in_wc" },
  { key: "chilled_water",   label: "Chilled water (CHW)",       kind: "liquid", flowUnits: "gpm",    pressureUnits: "psig" },
  { key: "heating_water",   label: "Heating hot water (HHW)",   kind: "liquid", flowUnits: "gpm",    pressureUnits: "psig" },
  { key: "dcw",             label: "Domestic cold water",       kind: "liquid", flowUnits: "gpm",    pressureUnits: "psig" },
  { key: "dhw",             label: "Domestic hot water",        kind: "liquid", flowUnits: "gpm",    pressureUnits: "psig" },
  { key: "rodi",            label: "RO/DI water",               kind: "liquid", flowUnits: "gpm",    pressureUnits: "psig" },
  { key: "ro",              label: "RO water",                  kind: "liquid", flowUnits: "gpm",    pressureUnits: "psig" },
  { key: "pw",              label: "Purified water (PW)",       kind: "liquid", flowUnits: "lpm",    pressureUnits: "bar" },
  { key: "wfi",             label: "Water for injection (WFI)", kind: "liquid", flowUnits: "lpm",    pressureUnits: "bar" },
  { key: "process_drain",   label: "Process drain",             kind: "drain",  flowUnits: "gpm",    pressureUnits: null },
  { key: "exhaust",         label: "Process exhaust",           kind: "air",    flowUnits: "cfm",    pressureUnits: "in_wc" },
  { key: "vent",            label: "Vent",                      kind: "air",    flowUnits: "cfm",    pressureUnits: "in_wc" },
];
export const UTILITY_BY_KEY = Object.fromEntries(UTILITY_TYPES.map(u => [u.key, u]));
export const isElectricalType = (key) => key === "electrical";
export const utilityLabel = (key) => UTILITY_BY_KEY[key]?.label || key || "—";

export const FLOW_UNITS = ["scfm", "slpm", "gpm", "lpm", "kg_hr", "lbs_hr", "cfm", "mbh", "cfh"];
export const PRESSURE_UNITS = ["psig", "bar", "in_wc", "in_hg"];
export const UNIT_LABELS = {
  scfm: "SCFM", slpm: "SLPM", gpm: "GPM", lpm: "LPM", kg_hr: "kg/hr", lbs_hr: "lbs/hr", cfm: "CFM", mbh: "MBH", cfh: "CFH",
  psig: "psig", bar: "bar", in_wc: "in. WC", in_hg: "in. Hg",
  gal_day: "gal/day", l_day: "L/day", kg_day: "kg/day",
};
export const unitLabel = (u) => UNIT_LABELS[u] || u || "";

// EquipmentConnections.PowerClass
export const POWER_CLASSES = [
  { key: "normal",    label: "Normal" },
  { key: "ups",       label: "UPS" },
  { key: "emergency", label: "Emergency" },
];

// ProcessEquipment.Status — lifecycle of the physical unit on THIS project
// (not an assumption/provenance status; that is a deferred P2 schema gap).
export const EQUIPMENT_STATUSES = [
  { key: "new",       label: "New" },
  { key: "existing",  label: "Existing" },
  { key: "future",    label: "Future" },
  { key: "relocated", label: "Relocated" },
];
export const statusLabel = (key) => EQUIPMENT_STATUSES.find(s => s.key === key)?.label || key || "—";

// ProcessEquipment.AssumptionStatus (P2 Phase B) — the PROVENANCE of a
// record's data, separate from the lifecycle Status above. Order = increasing
// verification; 'assumed' is the default for every estimate-stage entry.
// `proposal` is the simplified client-facing Basis published at the Proposal
// level (product decision 2026-09-12): it makes assumed vs confirmed
// requirements transparent to the client; detailed provenance, confidence,
// library source and override detail stay Internal / DD.
export const ASSUMPTION_STATUSES = [
  { key: "assumed",          label: "Assumed",           short: "Assumed",   verified: false, proposal: "Assumed" },
  { key: "manufacturer",     label: "Manufacturer data", short: "Mfr data",  verified: false, proposal: "Manufacturer data" },
  { key: "design_basis",     label: "Design basis",      short: "Design",    verified: false, proposal: "Design basis" },
  { key: "client_provided",  label: "Client-provided",   short: "Client",    verified: true,  proposal: "Owner-provided" },
  { key: "vendor_confirmed", label: "Vendor-confirmed",  short: "Vendor",    verified: true,  proposal: "Vendor-confirmed" },
  { key: "field_verified",   label: "Field-verified",    short: "Field",     verified: true,  proposal: "Field-verified" },
];
export const assumptionLabel = (key) => ASSUMPTION_STATUSES.find(s => s.key === (key || "assumed"))?.label || key || "—";
export const proposalBasisLabel = (key) => ASSUMPTION_STATUSES.find(s => s.key === (key || "assumed"))?.proposal || "Assumed";
export const isVerifiedAssumption = (key) => !!ASSUMPTION_STATUSES.find(s => s.key === key)?.verified;

// "60 × 48 × 78 in" from a record's dimension snapshot; null when none set.
export function dimensionsText(row) {
  const dims = [row?.WidthIn, row?.DepthIn, row?.HeightIn].map(v => (v === null || v === undefined || v === "" || !Number.isFinite(+v)) ? null : +v);
  if (dims.every(v => v === null)) return null;
  return `${dims.map(v => v === null ? "?" : Number(v.toFixed(1)).toLocaleString("en-US")).join(" × ")} in`;
}

// ProcessEquipment.FurnishedBy / InstalledBy
export const RESPONSIBLE_PARTIES = [
  { key: "owner",      label: "Owner" },
  { key: "cleanspace", label: "CleanSpace" },
  { key: "vendor",     label: "Vendor" },
];
export const partyLabel = (key) => RESPONSIBLE_PARTIES.find(p => p.key === key)?.label || key || "—";

// Categories already in use (MechanicalSystemsPanel's AHU / plant rows plus the
// process categories db/24's header names). Category stays free text — this
// list only feeds the picker's suggestions.
export const EQUIPMENT_CATEGORY_SUGGESTIONS = [
  "ahu", "dx_unit", "condensing_unit", "chiller", "boiler", "pump_package", "compressor", "vacuum_pump",
  "autoclave", "parts_washer", "isolator", "biosafety_cabinet", "laf_hood", "pass_through",
  "freezer", "incubator", "filling_line", "lyophilizer", "tank", "skid", "other",
];
